import { z } from "zod";
import type { FinancialYear, RuleSetMeta } from "../types.js";
import { Au13InputSchema } from "../au13/index.js";

/**
 * ccs-buffer — the CCS Withholding & Balancing-Debt Risk Check (candidate
 * `ccs-withholding-buffer-optimiser`).
 *
 * It is a thin, forward-looking projection layer over the SHIPPED au13 CCS rate
 * engine. CCS is paid through the year on the family's *lodged income estimate*;
 * Services Australia withholds a buffer (default 5%) of that subsidy. At
 * balancing, the subsidy is recomputed on *actual* adjusted taxable income — a
 * fresher projection of which the family enters here. If income rose, the family
 * was overpaid and faces a balancing DEBT (recovered at 20% of ongoing CCS if
 * still current); if it fell, a TOP-UP plus the withheld buffer is released.
 *
 * This module:
 *   - re-uses au13's `calculateAu13` to get the per-fortnight subsidy at BOTH
 *     the lodged estimate and the fresher projected income (never re-derives the
 *     CCS taper / rate-cap / higher-rate logic);
 *   - nets the chosen withholding buffer already held against the gross
 *     over/under-payment to project the balancing position;
 *   - recommends the withholding % that NEUTRALISES a projected debt.
 *
 * BINDING panel ship-conditions (encoded as code, not just comments):
 *   - Outputs are the user's OWN modelled estimate that Services Australia
 *     finalises — NEVER an industry-average dollar. The per-family "typical debt"
 *     figure is UNVERIFIED (figure 6); it is only ever surfaced as a "common
 *     outcome" qualitative band, never baked into the calc.
 *   - Information only, no advice, NO financial-product / affiliate / paid
 *     handoff may reach a result surface (`assertNoProductHandoff`, with a test).
 *   - The two levers (update estimate / change withholding %) are framed as
 *     options the family controls, with the "up to twice a year online" limit
 *     and the 2-year income-confirmation window stated.
 *
 * Pure: inputs -> rules -> outputs. No I/O, no Date.now(); the assessment FY is
 * always passed in.
 */

/**
 * One CCS care line for the family. Mirrors the au13 single-child input EXCEPT
 * `familyIncome`, which the buffer engine supplies twice (estimate vs projected)
 * — so it is omitted here and threaded in by the calculator.
 */
export const CcsBufferCareLineSchema = Au13InputSchema.omit({
  familyIncome: true,
});
export type CcsBufferCareLine = z.infer<typeof CcsBufferCareLineSchema>;

export const CcsBufferInputSchema = z.object({
  /**
   * The family income the CCS was PAID on during the year — the figure lodged
   * with Services Australia as the estimate. Drives the subsidy actually
   * received (and withheld against).
   */
  lodgedIncomeEstimate: z.number().min(0),
  /**
   * A fresher, best-guess projection of the family's ACTUAL adjusted taxable
   * income for the year (estimate + known mid-year changes). Drives the subsidy
   * the family is ultimately entitled to — what balancing recomputes against.
   */
  projectedActualIncome: z.number().min(0),
  /**
   * The withholding percentage CURRENTLY set, as a fraction. Services Australia's
   * default is 0.05 (5%); families can set 0–1. Clamped 0..1.
   */
  currentWithholdingRate: z.number().min(0).max(1).default(0.05),
  /**
   * Number of fortnights of subsidised care across the year the projection
   * spans (max 26 in a full FY). The annual figures are the per-fortnight
   * subsidy × this count.
   */
  fortnightsInCare: z.number().min(0).max(26).default(26),
  /**
   * The family's CCS care lines (≥ 1). Each is an au13 single-child input
   * WITHOUT income; the engine sums per-line subsidy at each income.
   */
  careLines: z.array(CcsBufferCareLineSchema).min(1),
});
/** Parsed (post-default) input — every field present. */
export type CcsBufferInput = z.infer<typeof CcsBufferInputSchema>;
/** Raw input accepted at the boundary — Zod-defaulted fields may be omitted. */
export type CcsBufferRawInput = z.input<typeof CcsBufferInputSchema>;

/** Which way the balancing lands. */
export type BalancingDirection = "debt" | "top_up" | "balanced";

export interface CcsBufferOutput {
  /** Total subsidy/fortnight summed across care lines, AT THE LODGED ESTIMATE. */
  subsidyPerFortnightAtEstimate: number;
  /** Total subsidy/fortnight summed across care lines, AT THE PROJECTION. */
  subsidyPerFortnightAtProjection: number;
  /** Annual subsidy PAID on the estimate (= estimate/fortnight × fortnights). */
  annualSubsidyPaid: number;
  /** Annual subsidy the projection ENTITLES the family to. */
  annualSubsidyEntitled: number;
  /**
   * Gross over/under-payment BEFORE the withholding buffer is applied. Positive
   * = overpaid (heading for a debt); negative = underpaid (top-up due).
   */
  grossBalancingAmount: number;
  /** Buffer already withheld over the year = withholding rate × annual paid. */
  withheldBuffer: number;
  /**
   * The projected balancing position AFTER netting the withheld buffer. Positive
   * = residual DEBT after the buffer is consumed; negative = TOP-UP the family
   * receives (the buffer is released on top — see `bufferReleased`). This is the
   * user's own modelled estimate; Services Australia finalises the actual amount.
   */
  projectedBalancingAmount: number;
  /** Direction of the projected balancing position. */
  direction: BalancingDirection;
  /**
   * On a TOP-UP (or balanced) outcome, the withheld buffer is released back to
   * the family; this is that released amount. 0 on a debt outcome (the buffer
   * was consumed offsetting it). */
  bufferReleased: number;
  /**
   * The debt-recovery profile IF a residual debt remains and CCS stays current:
   * Services Australia recovers it by withholding `recoveryRate` of ongoing CCS
   * payments. `null` when there is no residual debt.
   */
  recovery: {
    /** Fraction of ongoing CCS payments withheld to recover the debt (0.20). */
    recoveryRate: number;
    /** The residual debt the recovery applies to (= projectedBalancingAmount). */
    debtAmount: number;
  } | null;
  /**
   * The withholding % (fraction) that, applied across the year, would have
   * exactly neutralised the gross OVERPAYMENT — i.e. left zero residual debt at
   * balancing. `null` when there is no projected overpayment (nothing to
   * neutralise) or when no subsidy was paid to withhold against. Clamped to the
   * legal 0..1 range; if a debt is too large for even 100% withholding to cover,
   * this is 1 (capped) and a residual would remain.
   */
  neutralisingWithholdingRate: number | null;
}

/**
 * The withholding / balancing constants for one FY. The income taper itself is
 * NOT held here — it is delegated to the au13 ruleset pointed to by `au13Fy`.
 */
export interface CcsBufferRuleSet extends Omit<RuleSetMeta, "product"> {
  /** Shared-infra tag — not a single AU0x product (reuses au13's engine). */
  product: "SHARED";
  /** The au13 FY whose CCS rate engine this buffer ruleset pairs with. */
  au13Fy: FinancialYear;
  /** Services Australia default withholding buffer (fraction). 0.05 = 5%. */
  defaultWithholdingRate: number;
  /** Debt-recovery rate withheld from ongoing CCS when a debt is current. 0.20. */
  debtRecoveryRate: number;
  /** Times per year a family can change the withholding % online (myGov). */
  onlineWithholdingChangesPerYear: number;
  /**
   * Years after FY end within which income must be confirmed before CCS/ACCS is
   * cancelled and the overpayment becomes a debt (Account Payable Notice).
   */
  incomeConfirmationWindowYears: number;
}
